import moment from "moment";
import { countHours, timeConvert } from "my-time/utils";

export interface TimeItem {
  startTime: string;
  endTime: string;
  state?: "requested" | "approved" | string;
  hours: number;
  workBreakInMinutes: number;
}

export type MyTimeType =
  | "STAND_BY"
  | "LEAVE"
  | "ABSENCE"
  | "ON_CALL"
  | "WORKING_TIME"
  | "HOMEOFFICE"
  | "OVERTIME";

/**
 * A `TimeItem`-ből öröklöd a startTime/endTime/hours/workBreakInMinutes mezőket,
 * plusz ide jönnek a myTimeType, date stb.
 */
export interface MyTimeEntry extends TimeItem {
  date: string | Date;
  myTimeType: MyTimeType;
  timeDataTypeCode?: string;
  state?: string;
}

export interface CountDailyHoursParams {
  date: string | Date;
  list: MyTimeEntry[];
}

export interface CountDailyHoursResult {
  workTime: string;
  overtime: string;
  voluntaryOvertime: string;
  rawWorkTime: number;
  rawOvertime: number;
  rawVoluntaryOvertime: number;
}

/**
 * Aggregates daily hours by type for a given date.
 *
 * @param params.date - The date to filter entries by.
 * @param params.list - Array of time entries.
 * @returns Object containing formatted and raw totals.
 */
export function countDailyHours({
  date,
  list,
}: CountDailyHoursParams): CountDailyHoursResult {
  const targetDate = moment(date).format("YYYY-MM-DD");
  const dailyList = list.filter(
    (item) => moment(item.date).format("YYYY-MM-DD") === targetDate,
  );

  let rawWorkTime = 0;
  let rawOvertime = 0;
  let rawVoluntaryOvertime = 0;

  dailyList.forEach((item) => {
    switch (item.myTimeType) {
      case "WORKING_TIME":
      case "HOMEOFFICE":
        rawWorkTime += countHours({ ...item, state: item.state || "" });
        break;
      case "OVERTIME":
        if (item.timeDataTypeCode === "Tö") {
          rawVoluntaryOvertime += countHours({
            ...item,
            state: item.state || "",
          });
        } else if (item.state === "approved" || !item.state) {
          rawOvertime += countHours({ ...item, state: item.state || "" });
        }
        break;
      // Skip these types entirely
      case "STAND_BY":
      case "LEAVE":
      case "ABSENCE":
      case "ON_CALL":
      default:
        break;
    }
  });

  return {
    workTime: timeConvert(rawWorkTime),
    overtime: timeConvert(rawOvertime),
    voluntaryOvertime: timeConvert(rawVoluntaryOvertime),
    rawWorkTime,
    rawOvertime,
    rawVoluntaryOvertime,
  };
}

export default countDailyHours;
